import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {makeStyles} from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import {Alert} from "@material-ui/lab";
import LightBulbShine from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/lamps-lights-fire/light-bubbles/light-bulb-shine.svg";
import AlertTriangle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/alerts/alert-triangle.svg";
import QuestionCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/alerts/question-circle.svg";
import CheckCircle1 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/form-validation/check-circle-1.svg";
import StreamlineIcon from "../streamline-icon/StreamlineIcon";
import {useI18N} from "../../../common/common";

const useStyles = makeStyles((theme) => ({
    defaultStyle: {
        width: "100%",
        borderRadius: theme.spacing(0),
        "& > .MuiAlert-message": {
            width: "100%"
        }
    },
    alertMessage: {
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.25),
    },
    defaultTitle: {
        float: "left",
        paddingTop: theme.spacing(0.5),
        paddingBottom: theme.spacing(0.25),
        paddingRight: theme.spacing(1),
    },
    defaultIcon: {
        marginTop: theme.spacing(0.25)
    },
    errorStyle: {
        background: theme.palette.error.light,
        border: "2px solid " + theme.palette.error.light,

    },
    warningStyle: {
        background: theme.palette.secondary["A200"],
        border: "2px solid " + theme.palette.secondary["A200"],

    },
    successStyle: {
        background: theme.palette.primary["100"],
        border: "2px solid " + theme.palette.primary["100"],

    },
    infoStyle: {
        background: theme.palette.secondary["100"],
        border: "2px solid " + theme.palette.secondary["100"],
        "& i": {
            color: theme.palette.grey[400],
        },
    },
    outlinedStyle: {
        background: "none !important",
        "& i": {
            color: "inherit",
        },
    },
}));


const CustomAlert = ({icon, className, children, type, noTypeText, variant, title, textClass}) => {

    const classes = useStyles();
    const {i18n} = useI18N();

    const renderIcon = (type) => {
        if(typeof(icon) === "string") {
            switch (type) {
                case "error": return (
                    <StreamlineIcon
                        className={classes.defaultIcon}
                    >
                        <AlertTriangle/>
                    </StreamlineIcon>
                );
                case "warning": return (
                    <StreamlineIcon
                        className={classes.defaultIcon}
                    >
                        <QuestionCircle/>
                    </StreamlineIcon>
                );
                case "info": return (
                    <StreamlineIcon
                        className={classes.defaultIcon}
                    >
                        <LightBulbShine/>
                    </StreamlineIcon>
                );
                case "success": return (
                    <StreamlineIcon
                        className={classes.defaultIcon}
                    >
                        <CheckCircle1/>
                    </StreamlineIcon>
                );
                default: return false;
            }
        } else {
            return icon;
        }
    };

    const renderText = (type) => {
        if(title) {
            return title;
        }
        else if (noTypeText) {
            return false;
        } else {
            // TODO: add also the translations for Warning and Info
            switch (type) {
                case "error": return i18n("common.label.error");
                case "warning": return "Warning";
                case "info": return "Info";
                case "success": return i18n("common.label.success");
                default: return false;
            }
        }
    };

    return (
        <Alert
            className={clsx(classes.defaultStyle, className,
                { [classes.errorStyle]: type === "error" },
                { [classes.warningStyle]: type === "warning" },
                { [classes.infoStyle]: type === "info"},
                { [classes.successStyle]: type === "success"},
                { [classes.outlinedStyle]: variant === "outlined"},
            )}
            icon={icon && renderIcon(type)}
            severity={type}
            variant={"outlined"}
        >
            {!noTypeText &&
            <Typography
                className={classes.defaultTitle}
                variant="subtitle1"
                component="span"
            >
                {renderText(type)}
            </Typography>}
            <div
                className={textClass || classes.alertMessage}
            >
                {children}
            </div>
        </Alert>
    );
};


CustomAlert.defaultProps = {
    type: "error",
    variant: "",
    noTypeText: false
};

CustomAlert.propTypes = {
    children: PropTypes.any,
    icon: PropTypes.any,
    className: PropTypes.any,
    type: PropTypes.string,
    variant: PropTypes.string,
    title: PropTypes.string,
    textClass: PropTypes.any,
    noTypeText: PropTypes.bool
};

export default CustomAlert;
